/**
 * Keeps THIS tab pinned to the ACCOUNT it is showing, by userId, across registry
 * mutations made in OTHER tabs.
 *
 * ── The bug this fixes ───────────────────────────────────────────────────────
 * The per-tab active account is encoded as the `/u/<n>` URL index into the
 * SHARED accounts registry (`space_accounts`). The index is positional, so when
 * another tab REMOVES an account (sign-out with remaining accounts, or
 * remove-account), every account after it shifts down one slot. A tab whose URL
 * still says `/u/<n>` then silently points at a DIFFERENT account than the one it
 * was showing — e.g. with `[A, B, guest]`, tab T2 at `/u/1` shows B; after A is
 * signed out elsewhere the registry becomes `[B, guest]`, so `/u/1` now resolves
 * to the GUEST and T2 appears to "lose" B's data (B is safe — server + its own
 * per-user DB — but the tab is looking at the wrong account).
 *
 * ── The fix ──────────────────────────────────────────────────────────────────
 * We remember the userId each tab last resolved (its "pinned" account). When the
 * registry changes (cross-tab `storage` event or a same-tab mutation that emits a
 * location/registry change), we re-resolve the tab's URL index:
 *   - if the index still maps to the pinned userId → nothing to do;
 *   - if the pinned account MOVED (still present at a different index) → rewrite
 *     the URL to its new index via `history.replaceState`, so the tab keeps
 *     showing the same account (Gmail keeps your window on your inbox when you
 *     sign another account out elsewhere);
 *   - if the pinned account is GONE (this tab's own account was the one removed)
 *     → leave the URL; the normal clamp/redirect handles the fallback.
 *
 * Router-agnostic + lifetime singleton (mirrors `reactive-location.ts`): it runs
 * above the router, observes `storage` + history, and rewrites via the History
 * API (which `reactive-location` already listens to, so the PowerSync DB-binding
 * effect re-runs and swaps the displayed account without a reload).
 */

import { readAccountsStateSync } from "@/lib/accounts";
import {
  buildUPath,
  indexOfUserId,
  parseActiveIndexFromPath,
  resolveTabAccount,
  stripUPrefix,
} from "@/lib/active-account";

const ACCOUNTS_STORAGE_KEY = "space_accounts";
const LOCATION_CHANGE_EVENT = "space:locationchange";

let installed = false;
/** The userId (or guest sentinel) this tab is currently pinned to. */
let pinnedUserId: string | null = null;

/** A stable key for an account: real → userId, guest → "guest". */
function accountKey(account: { kind: string; userId: string } | null): string | null {
  if (!account) {
    return null;
  }
  return account.kind === "guest" ? "guest" : account.userId;
}

/** Re-reads this tab's resolved account and remembers it as the pin. */
function refreshPin(): void {
  const account = resolveTabAccount(readAccountsStateSync());
  pinnedUserId = accountKey(account);
}

/**
 * Reconciles the URL with the pinned account after a registry change. If the
 * pinned account still exists but moved to a new index, rewrite the URL to it.
 */
function reconcile(): void {
  if (pinnedUserId === null) {
    refreshPin();
    return;
  }
  const state = readAccountsStateSync();
  const urlIndex = parseActiveIndexFromPath(globalThis.location.pathname);
  if (urlIndex === null) {
    // Not on a `/u/<n>` path (auth page etc.) — nothing to pin.
    return;
  }

  const currentAtUrl = accountKey(resolveTabAccount(state, urlIndex));
  if (currentAtUrl === pinnedUserId) {
    // URL still points at the pinned account — in sync.
    return;
  }

  // The URL index drifted. Find where the pinned account lives now.
  const newIndex =
    pinnedUserId === "guest"
      ? state.accounts.findIndex((a) => a.kind === "guest")
      : indexOfUserId(state, pinnedUserId);

  if (newIndex !== null && newIndex !== -1) {
    // Pinned account still exists at a new index → follow it (no reload).
    const inner = stripUPrefix(globalThis.location.pathname);
    const search = globalThis.location.search ?? "";
    const target = `${buildUPath(newIndex, inner)}${search}`;
    globalThis.history.replaceState(globalThis.history.state, "", target);
    // `replaceState` is monkeypatched by reactive-location to emit the change.
    return;
  }

  // Pinned account is gone (this tab's own account was removed): re-pin to
  // whatever the URL now resolves to so we don't keep fighting the clamp.
  refreshPin();
}

/**
 * Installs the lifetime singleton listeners. Idempotent. Call once at app boot
 * (from the PowerSync provider, which is mounted app-wide above the router).
 */
export function installTabAccountPin(): void {
  if (installed || typeof globalThis.addEventListener !== "function") {
    return;
  }
  installed = true;
  refreshPin();

  // Cross-tab registry mutations (another tab signs out / removes an account).
  globalThis.addEventListener("storage", (event: StorageEvent) => {
    if (event.key === ACCOUNTS_STORAGE_KEY || event.key === null) {
      reconcile();
    }
  });

  // Same-tab navigation: re-pin to the freshly navigated account (a switcher
  // navigation is an explicit account change for THIS tab).
  globalThis.addEventListener(LOCATION_CHANGE_EVENT, refreshPin);
  globalThis.addEventListener("popstate", refreshPin);
}
